import React from "react";
import { IoPlayCircleOutline, IoTrendingUpOutline } from "react-icons/io5";
import { useReveal } from "../../hooks/useReveal";
import { asIcon } from "./ProjectSection";
import styles from "./ApproachSection.module.css";

type Shot = {
  src: string;
  alt: string;
  /** Which tile of the staggered trio this photo fills. */
  tile: string;
};

const SHOTS: readonly Shot[] = [
  {
    src: "/images/product/horizontal-bowl-activity-new.png",
    alt: "A hand holding the 3D-printed bowl with a phone seated inside it",
    tile: "tallLeft",
  },
  {
    src: "/images/product/vertical-mug-activity-new.png",
    alt: "A person raising the 3D-printed mug overhead during an upper-limb exercise",
    tile: "wideCenter",
  },
  {
    src: "/images/product/mug-still-shot.jpeg",
    alt: "The 3D-printed mug on a desk with the mRehab app open on the phone inside",
    tile: "tallRight",
  },
];

type ApproachItem = {
  title: string;
  body: string;
  icon: React.ElementType;
};

const ITEMS: readonly ApproachItem[] = [
  {
    title: "Show me first",
    body: "Every activity comes with a short demo video, so you always know what a good rep looks like.",
    icon: asIcon(IoPlayCircleOutline),
  },
  {
    title: "Start small, keep going",
    body: "Training is gradual and supportive. Short sessions build into a habit you can actually keep.",
    icon: asIcon(IoTrendingUpOutline),
  },
];

/**
 * At home (`#at-home`)
 */
export function ApproachSection() {
  const ref = useReveal<HTMLElement>();
  const galleryRef = useReveal<HTMLDivElement>();

  return (
    <section
      className={`${styles.band} ui-reveal`}
      id="at-home"
      aria-labelledby="at-home-title"
      ref={ref}
    >
      <div className="ui-band-inner">
        <div className={styles.head}>
          <h2 id="at-home-title" className={styles.title}>
            Practice at your kitchen table, not in a clinic
          </h2>
          <p className={styles.lead}>
            Real objects, real motions. Your phone keeps count while you get on
            with your day.
          </p>
        </div>

        <div className={`${styles.gallery} ui-reveal--stagger`} ref={galleryRef}>
          {SHOTS.map((shot, i) => (
            <figure
              key={shot.src}
              className={`${styles.tile} ${styles[shot.tile]}`}
              style={{ "--reveal-index": i } as React.CSSProperties}
            >
              <img src={shot.src} alt={shot.alt} loading="lazy" decoding="async" />
            </figure>
          ))}
        </div>

        <div className={styles.notes}>
          {ITEMS.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.title} className={styles.note}>
                <Icon aria-hidden className={styles.noteIcon} />
                <div>
                  <span className={styles.noteTitle}>{item.title}</span>
                  <p className={styles.noteBody}>{item.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
